import type {
  DocumentStateMerchantOption,
  DocumentStateOption,
} from "~/types/ReportingDocumentStateExport";

const getDisplayName = (item: Record<string, any>): string => {
  if (typeof item.name === "string") {
    return item.name;
  }

  if (typeof item.label === "string") {
    return item.label;
  }

  if (item.name && typeof item.name === "object") {
    return item.name.fr || item.name.en || String(item.id);
  }

  return String(item.id);
};

export const useDocumentStateExportOptions = () => {
  const client = useSanctumClient();

  const documentTypeOptions = ref<DocumentStateOption[]>([]);
  const generatedByOptions = ref<DocumentStateOption[]>([]);
  const merchantOptions = ref<DocumentStateMerchantOption[]>([]);
  const isLoading = ref(false);
  const isLoadingMerchants = ref(false);
  const lastLoadedMerchantCityId = ref<number | null>(null);

  const getMerchantMarketIds = (item: Record<string, any>): number[] => {
    const directMarketIds = Array.isArray(item.marketIds)
      ? item.marketIds
      : Array.isArray(item.market_ids)
        ? item.market_ids
        : [];

    const relatedMarketIds = Array.isArray(item.markets)
      ? item.markets.map((market: Record<string, any>) =>
        Number(market?.id ?? market?.market_id ?? market?.value),
      )
      : [];

    return Array.from(
      new Set(
        [...directMarketIds, ...relatedMarketIds]
          .map((id) => Number(id))
          .filter((id) => Number.isFinite(id) && id > 0),
      ),
    );
  };

  const loadDocumentTypes = async () => {
    const response = await client("/api/mediaTypes/search?no_pagination=true", {
      method: "post",
      body: {
        scopes: [
          { name: "requiredForMerchantsOrCompanies" }
        ],
        sort: [
          { field: "name", direction: "asc" }
        ]
      }
    });

    documentTypeOptions.value = (response?.data || [])
      .map((item: Record<string, any>) => ({
        name: getDisplayName(item),
        value: Number(item.id),
      }))
      .filter((item: DocumentStateOption) => Number.isFinite(item.value))
      .sort((a: DocumentStateOption, b: DocumentStateOption) =>
        a.name.localeCompare(b.name, "fr", { sensitivity: "base" }),
      );
  };

  const loadGeneratedByUsers = async () => {
    const response = await client("/api/users", {
      query: { no_pagination: true },
    });

    generatedByOptions.value = (response?.data || [])
      .filter((item: Record<string, any>) => item?.id > 0 && item?.disabled !== 1)
      .map((item: Record<string, any>) => ({
        name: getDisplayName(item),
        value: Number(item.id),
      }))
      .filter((item: DocumentStateOption) => Number.isFinite(item.value))
      .sort((a: DocumentStateOption, b: DocumentStateOption) =>
        a.name.localeCompare(b.name, "fr", { sensitivity: "base" }),
      );
  };

  const load = async () => {
    if (isLoading.value) {
      return;
    }

    isLoading.value = true;

    try {
      await Promise.all([loadDocumentTypes(), loadGeneratedByUsers()]);
    } catch (error) {
      documentTypeOptions.value = [];
      generatedByOptions.value = [];
      console.error("Erreur chargement options export documents:", error);
    } finally {
      isLoading.value = false;
    }
  };

  const loadMerchantsByCity = async (cityId: number | null) => {
    if (!cityId || Number(cityId) <= 0) {
      lastLoadedMerchantCityId.value = null;
      merchantOptions.value = [];
      return;
    }

    if (lastLoadedMerchantCityId.value === cityId) {
      return;
    }

    isLoadingMerchants.value = true;

    try {
      const response = await client(`/api/cities/${cityId}/merchants`, {
        query: {
          no_pagination: true,
          with_market_ids: true,
          only: "merchants.name,merchants.id"
        },
      });

      merchantOptions.value = (response?.data || [])
        .map((item: Record<string, any>) => ({
          name: getDisplayName(item),
          value: Number(item.id),
          marketIds: getMerchantMarketIds(item),
        }))
        .filter((item: DocumentStateMerchantOption) => Number.isFinite(item.value))
        .sort(
          (a: DocumentStateMerchantOption, b: DocumentStateMerchantOption) =>
            a.name.localeCompare(b.name, "fr", { sensitivity: "base" }),
        );

      lastLoadedMerchantCityId.value = cityId;
    } catch (error) {
      lastLoadedMerchantCityId.value = null;
      merchantOptions.value = [];
      console.error("Erreur chargement commerçants filtrés par ville:", error);
    } finally {
      isLoadingMerchants.value = false;
    }
  };

  return {
    documentTypeOptions,
    generatedByOptions,
    merchantOptions,
    isLoading,
    isLoadingMerchants,
    load,
    loadMerchantsByCity,
  };
};
